import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatMoney, formatDate } from "@/lib/format";
import { EXPENSE_CATEGORIES, INCOME_CATEGORIES } from "@/lib/entities";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Check,
  Loader2,
  Link2,
  EyeOff,
  Sparkles,
  RotateCcw,
  Pencil,
  Trash2,
  Undo2,
  Plus,
  Landmark,
  Split,
  CalendarClock,
} from "lucide-react";
import { BankTxnDialog } from "@/components/BankTxnDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_TABS = [
  { id: "unmatched", label: "Needs matching" },
  { id: "posted", label: "Posted" },
  { id: "ignored", label: "Ignored" },
  { id: "all", label: "All" },
];

const CONF_STYLE = {
  high: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  medium: "text-amber-300 border-amber-400/40 bg-amber-400/10",
  low: "text-muted-foreground border-white/15 bg-white/5",
};

const TDS_SECTIONS = ["194C", "194J", "194I", "194H", "194A", "194Q", "Other"];

function SuggestionRow({ txn, credit, s, busyId, onConfirm }) {
  const ded = s.deduction;
  const [applyDed, setApplyDed] = useState(!!(ded && ded.gap > 0));
  const [dedAmt, setDedAmt] = useState(ded?.gap || 0);
  const [section, setSection] = useState(() => {
    const first = (ded?.section || "").split("/")[0];
    return TDS_SECTIONS.includes(first) ? first : ded?.section ? "Other" : "194C";
  });

  const confirmNow = () =>
    onConfirm(
      s,
      applyDed && Number(dedAmt) > 0 ? { amount: Number(dedAmt), section, type: "tds" } : null
    );

  return (
    <div
      className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2 space-y-2"
      data-testid={`suggestion-${txn.id}-${s.target_id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm truncate">
            {s.party} {s.invoice_no ? <span className="text-muted-foreground">· {s.invoice_no}</span> : null}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {s.category && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-brand/10 text-brand border-brand/25">
                {s.category}
              </Badge>
            )}
            <p className="text-[11px] text-muted-foreground">{s.why}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs">{formatMoney(s.remaining, s.currency)}</span>
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${CONF_STYLE[s.confidence]}`}
          >
            {s.confidence}
          </span>
          <Button
            size="sm"
            onClick={confirmNow}
            disabled={!!busyId}
            className="gap-1.5"
            data-testid={`confirm-match-${txn.id}-${s.target_id}`}
          >
            {busyId === s.target_id ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Link2 className="h-3.5 w-3.5" />
            )}
            Confirm match
          </Button>
        </div>
      </div>

      {ded && ded.gap > 0 && (
        <div
          className="rounded-md border border-amber-400/20 bg-amber-400/[0.04] px-3 py-2 space-y-2"
          data-testid={`deduction-panel-${txn.id}-${s.target_id}`}
        >
          <label className="flex items-start gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={applyDed}
              onChange={(e) => setApplyDed(e.target.checked)}
              className="mt-0.5"
              data-testid={`deduction-toggle-${txn.id}-${s.target_id}`}
            />
            <span>
              Bank is short by{" "}
              <span className="font-mono text-amber-300">{formatMoney(ded.gap, s.currency)}</span>{" "}
              {ded.validated ? (
                <>
                  — matches{" "}
                  <span className="text-emerald-300">
                    TDS {ded.section} @ {ded.rate}%
                  </span>{" "}
                  ✓. Book it to TDS {credit ? "Receivable (26AS)" : "Payable"} and close the invoice fully.
                </>
              ) : (
                <>— unverified deduction. Confirm the section &amp; amount to book it as TDS and close fully.</>
              )}
            </span>
          </label>
          {applyDed && (
            <div className="flex flex-wrap items-center gap-2 pl-6">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">TDS amount</span>
              <Input
                type="number"
                value={dedAmt}
                onChange={(e) => setDedAmt(e.target.value)}
                className="h-8 w-32"
                data-testid={`deduction-amount-${txn.id}-${s.target_id}`}
              />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Section</span>
              <Select value={section} onValueChange={setSection}>
                <SelectTrigger className="h-8 w-28" data-testid={`deduction-section-${txn.id}-${s.target_id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TDS_SECTIONS.map((x) => (
                    <SelectItem key={x} value={x}>
                      {x}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DirectPost({ txn, onDone }) {
  const credit = txn.direction === "credit";
  const [type, setType] = useState(credit ? "income" : "expense");
  const [category, setCategory] = useState(txn.suggested_category || "Other");
  const [party, setParty] = useState(txn.vendor || "");
  const [busy, setBusy] = useState(false);
  const cats = type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/recon/bank-txns/${txn.id}/post-direct`, {
        type,
        category,
        party: party || txn.description,
      });
      toast.success(`Posted as ${type}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-2 pt-3 mt-3 border-t border-white/5">
      <div className="space-y-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Book directly as</p>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-[150px] h-9" data-testid={`direct-type-${txn.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="expense">Direct expense</SelectItem>
            <SelectItem value="income">Other income</SelectItem>
            <SelectItem value="transfer">Internal transfer</SelectItem>
            <SelectItem value="sweep">Sweep movement</SelectItem>
            <SelectItem value="fd">Fixed deposit</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {(type === "expense" || type === "income") && (
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Head</p>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[190px] h-9" data-testid={`direct-category-${txn.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {cats.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Party</p>
        <Input
          value={party}
          onChange={(e) => setParty(e.target.value)}
          placeholder="Vendor / source"
          className="h-9 w-[200px]"
          data-testid={`direct-party-${txn.id}`}
        />
      </div>
      <Button
        size="sm"
        variant="secondary"
        onClick={submit}
        disabled={busy}
        className="gap-2"
        data-testid={`direct-post-btn-${txn.id}`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        Post without invoice
      </Button>
    </div>
  );
}

function SplitDialog({ open, onOpenChange, txn, credit, onDone }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const bankAmt = Number(txn.amount) || 0;
  const ccy = txn.currency || "INR";

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      setSel({});
      setQ("");
      try {
        const { data } = await api.get(`/recon/open-items?kind=${credit ? "receivable" : "payable"}`);
        setItems(data.items || []);
      } catch {
        toast.error("Could not load open invoices");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, credit]);

  const toggle = (it) =>
    setSel((prev) => {
      const next = { ...prev };
      if (next[it.id]) delete next[it.id];
      else next[it.id] = { amount: it.remaining, tds: 0, section: "194C" };
      return next;
    });
  const setField = (id, k, v, remaining) =>
    setSel((prev) => {
      const row = { ...prev[id], [k]: v };
      if (k === "tds") row.amount = Math.max((remaining || 0) - (Number(v) || 0), 0);
      return { ...prev, [id]: row };
    });

  const allocated = Object.values(sel).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const remaining = Math.round((bankAmt - allocated) * 100) / 100;
  const count = Object.keys(sel).length;
  const canConfirm = count >= 2 && Math.abs(remaining) <= 1;
  const filtered = items.filter(
    (it) => !q || `${it.party} ${it.invoice_no}`.toLowerCase().includes(q.toLowerCase())
  );

  const submit = async () => {
    setBusy(true);
    try {
      const allocations = Object.entries(sel).map(([target_id, r]) => ({
        target_id,
        amount: Number(r.amount) || 0,
        deduction: Number(r.tds) > 0 ? { amount: Number(r.tds), section: r.section, type: "tds" } : null,
      }));
      const { data } = await api.post(`/recon/bank-txns/${txn.id}/confirm-split`, { allocations });
      toast.success(`Settled ${data.count} invoices from one bank line`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not split the payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col"
        data-testid={`split-dialog-${txn.id}`}
      >
        <DialogHeader>
          <DialogTitle>Pay multiple invoices — {formatMoney(bankAmt, ccy)}</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Search vendor / invoice…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-9"
          data-testid={`split-search-${txn.id}`}
        />
        <div className="flex-1 overflow-y-auto rounded-md border border-border divide-y divide-border min-h-[160px]">
          {loading ? (
            <div className="p-6 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-brand" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No open {credit ? "receivables" : "payables"}.
            </div>
          ) : (
            filtered.map((it) => {
              const row = sel[it.id];
              return (
                <div key={it.id} className="px-3 py-2" data-testid={`split-item-${it.id}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!row}
                      onChange={() => toggle(it)}
                      data-testid={`split-check-${it.id}`}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="text-sm truncate">
                        {it.party}
                        {it.invoice_no ? ` · ${it.invoice_no}` : ""}
                      </span>
                      <span className="block text-[11px] text-muted-foreground">
                        open {formatMoney(it.remaining, it.currency)}
                        {it.category ? ` · ${it.category}` : ""}
                      </span>
                    </span>
                  </label>
                  {row && (
                    <div className="mt-2 pl-6 flex flex-wrap items-center gap-2">
                      <span className="text-[10px] uppercase text-muted-foreground">Cash paid</span>
                      <Input
                        type="number"
                        value={row.amount}
                        onChange={(e) => setField(it.id, "amount", e.target.value, it.remaining)}
                        className="h-8 w-28"
                        data-testid={`split-amount-${it.id}`}
                      />
                      <span className="text-[10px] uppercase text-muted-foreground">TDS</span>
                      <Input
                        type="number"
                        value={row.tds}
                        onChange={(e) => setField(it.id, "tds", e.target.value, it.remaining)}
                        className="h-8 w-24"
                        data-testid={`split-tds-${it.id}`}
                      />
                      {Number(row.tds) > 0 && (
                        <Select value={row.section} onValueChange={(v) => setField(it.id, "section", v, it.remaining)}>
                          <SelectTrigger className="h-8 w-24" data-testid={`split-section-${it.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TDS_SECTIONS.map((x) => (
                              <SelectItem key={x} value={x}>
                                {x}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div
          className={`text-sm flex items-center justify-between ${
            Math.abs(remaining) <= 1 && count >= 2 ? "text-emerald-400" : "text-amber-400"
          }`}
          data-testid={`split-tally-${txn.id}`}
        >
          <span>
            {count} selected · allocated {formatMoney(allocated, ccy)}
          </span>
          <span>
            {Math.abs(remaining) <= 1
              ? count >= 2
                ? "✓ matches bank line"
                : "select at least 2 invoices"
              : `${formatMoney(remaining, ccy)} left to allocate`}
          </span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canConfirm || busy} className="gap-2" data-testid={`split-confirm-${txn.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Split className="h-4 w-4" />}
            Settle {count || ""} invoices
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TxnCard({ txn, onRefresh, onEdit }) {
  const credit = txn.direction === "credit";
  const [busyId, setBusyId] = useState("");
  const [showDirect, setShowDirect] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const confirm = async (s, deduction = null) => {
    setBusyId(s.target_id);
    try {
      await api.post(`/recon/bank-txns/${txn.id}/confirm`, {
        kind: s.kind,
        target_id: s.target_id,
        amount: txn.amount,
        deduction,
      });
      const baseEntry =
        s.kind === "payable"
          ? "Dr Accounts Payable / Cr Bank"
          : s.kind === "expense"
          ? "Dr Payroll / Salaries Payable / Cr Bank"
          : "Dr Bank / Cr Accounts Receivable";
      const tdsLeg = deduction
        ? s.kind === "payable"
          ? " / Cr TDS Payable"
          : " + Dr TDS Receivable"
        : "";
      toast.success(`Settled ${s.invoice_no || s.party} — journal posted (${baseEntry}${tdsLeg})`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm match");
    } finally {
      setBusyId("");
    }
  };

  const ignore = async () => {
    setBusyId("ignore");
    try {
      await api.post(`/recon/bank-txns/${txn.id}/ignore`, { reason: "Not a book entry" });
      toast.success("Line parked as ignored");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not ignore");
    } finally {
      setBusyId("");
    }
  };

  const reopen = async () => {
    setBusyId("reopen");
    try {
      await api.post(`/recon/bank-txns/${txn.id}/reopen`);
      toast.success("Line reopened for matching");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reopen");
    } finally {
      setBusyId("");
    }
  };

  const unpost = async () => {
    setBusyId("unpost");
    try {
      const { data } = await api.post(`/recon/bank-txns/${txn.id}/unpost`);
      toast.success(`Reversed the ${data.reversed} posting — line is matchable again`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not unpost");
    } finally {
      setBusyId("");
    }
  };

  const remove = async () => {
    setBusyId("delete");
    try {
      const { data } = await api.delete(
        `/recon/bank-txns/${txn.id}?cascade=${txn.status === "posted" ? "true" : "false"}`
      );
      toast.success(
        `Bank transaction deleted${data.reversed ? ` · ${data.reversed} posting reversed` : ""} · balance adjusted`
      );
      setConfirmDelete(false);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally {
      setBusyId("");
    }
  };

  const rowActions = (
    <div className="flex items-center gap-1">
      {txn.status === "posted" ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={unpost}
          disabled={!!busyId}
          className="gap-1.5 text-muted-foreground h-8"
          title="Reverse the posting and make this line matchable again"
          data-testid={`unpost-txn-${txn.id}`}
        >
          <Undo2 className="h-3.5 w-3.5" />
          Unpost
        </Button>
      ) : (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onEdit(txn)}
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          title="Edit this bank line"
          data-testid={`edit-txn-${txn.id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
      <Button
        size="icon"
        variant="ghost"
        onClick={() => setConfirmDelete(true)}
        className="h-8 w-8 text-muted-foreground hover:text-rose-300"
        title="Delete this bank line"
        data-testid={`delete-txn-${txn.id}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );

  return (
    <Card className="p-4 space-y-3" data-testid={`bank-txn-${txn.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`h-9 w-9 shrink-0 rounded-md flex items-center justify-center border ${
              credit
                ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-300"
                : "bg-rose-400/10 border-rose-400/30 text-rose-300"
            }`}
          >
            {credit ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" title={txn.description}>
              {txn.vendor || txn.description || "Bank line"}
            </p>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {(txn.category || txn.suggested_category) && (
                <Badge
                  variant="outline"
                  className="text-[10px] px-1.5 py-0 h-5 bg-brand/10 text-brand border-brand/25"
                  data-testid={`bank-txn-category-${txn.id}`}
                >
                  {txn.category || txn.suggested_category}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground truncate">
                {formatDate(txn.date)} · {txn.account_name}
                {txn.invoice_no ? ` · inv ${txn.invoice_no}` : ""}
              </span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <p
            className={`font-mono text-sm ${credit ? "text-emerald-300" : "text-rose-300"}`}
            data-testid={`bank-txn-amount-${txn.id}`}
          >
            {credit ? "+" : "−"}
            {formatMoney(txn.amount, txn.currency)}
          </p>
          <div className="flex items-center justify-end gap-2">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">
              {txn.status}
              {txn.entry_mode === "manual" ? " · manual" : ""}
            </p>
            {rowActions}
          </div>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid={`delete-dialog-${txn.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this bank transaction?</AlertDialogTitle>
            <AlertDialogDescription>
              {formatMoney(txn.amount, txn.currency)} dated {formatDate(txn.date)} on{" "}
              {txn.account_name}. The bank balance will be adjusted back
              {txn.status === "posted"
                ? ", and the ledger entry it was posted to will be reversed."
                : "."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`delete-cancel-${txn.id}`}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                remove();
              }}
              data-testid={`delete-confirm-${txn.id}`}
              className="bg-rose-500 hover:bg-rose-600 text-white"
            >
              {busyId === "delete" ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {txn.status === "unmatched" && (
        <>
          {txn.suggestions?.length ? (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Suggested {credit ? "client invoices" : "vendor invoices"}
              </p>
              {txn.suggestions.map((s) => (
                <SuggestionRow
                  key={s.target_id}
                  txn={txn}
                  credit={credit}
                  s={s}
                  busyId={busyId}
                  onConfirm={confirm}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              No open invoice looks like this payment — book it directly below.
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDirect((v) => !v)}
              data-testid={`toggle-direct-${txn.id}`}
            >
              {showDirect ? "Hide direct posting" : "Book without invoice"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowSplit(true)}
              className="gap-1.5"
              data-testid={`split-txn-${txn.id}`}
            >
              <Split className="h-3.5 w-3.5" /> Pay multiple invoices
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={ignore}
              disabled={!!busyId}
              className="gap-1.5 text-muted-foreground"
              data-testid={`ignore-txn-${txn.id}`}
            >
              <EyeOff className="h-3.5 w-3.5" />
              Ignore
            </Button>
          </div>
          {showDirect && <DirectPost txn={txn} onDone={onRefresh} />}
          <SplitDialog open={showSplit} onOpenChange={setShowSplit} txn={txn} credit={credit} onDone={onRefresh} />
        </>
      )}

      {txn.status === "posted" && txn.match && (
        <p className="text-xs text-muted-foreground" data-testid={`posted-info-${txn.id}`}>
          Posted as <span className="text-foreground">{txn.match.kind}</span>
          {txn.match.invoice_no ? ` · invoice ${txn.match.invoice_no}` : ""}
          {txn.match.party ? ` · ${txn.match.party}` : ""}
          {txn.match.deduction
            ? ` · incl. TDS ${txn.match.deduction.section || ""} ${formatMoney(
                txn.match.deduction.amount,
                txn.currency
              )}`
            : ""}{" "}
          by {txn.posted_by}
        </p>
      )}

      {txn.status === "ignored" && (
        <Button
          size="sm"
          variant="outline"
          onClick={reopen}
          disabled={!!busyId}
          className="gap-1.5"
          data-testid={`reopen-txn-${txn.id}`}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reopen for matching
        </Button>
      )}
    </Card>
  );
}

function RemitTdsDialog({ open, onOpenChange, accounts, tds, onRemitted }) {
  const [bankId, setBankId] = useState("");
  const [amount, setAmount] = useState(0);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [section, setSection] = useState("ALL");
  const [challan, setChallan] = useState("");
  const [busy, setBusy] = useState(false);
  const sections = tds?.payable?.by_section || [];
  const ccy = accounts?.[0]?.currency || tds?.currency || "INR";

  useEffect(() => {
    if (open) {
      setBankId(accounts[0]?.id || "");
      setAmount(tds?.payable?.outstanding || 0);
      setSection("ALL");
      setChallan("");
      setDate(new Date().toISOString().slice(0, 10));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!bankId) return toast.error("Select a bank account");
    if (!(Number(amount) > 0)) return toast.error("Enter an amount");
    setBusy(true);
    try {
      await api.post("/recon/tds/remit", {
        bank_account_id: bankId,
        amount: Number(amount),
        date,
        section: section === "ALL" ? "" : section,
        challan_no: challan,
      });
      toast.success("TDS remitted — journal posted (Dr TDS Payable / Cr Bank)");
      onOpenChange(false);
      onRemitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remittance failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="remit-tds-dialog">
        <DialogHeader>
          <DialogTitle>Remit TDS to Government</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">From bank account</label>
            <Select value={bankId} onValueChange={setBankId}>
              <SelectTrigger className="mt-1" data-testid="remit-bank-select">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Amount</label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1"
                data-testid="remit-amount"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Date</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1"
                data-testid="remit-date"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Section (optional)</label>
              <Select
                value={section}
                onValueChange={(v) => {
                  setSection(v);
                  const row = sections.find((r) => r.section === v);
                  if (row) setAmount(row.outstanding);
                  else setAmount(tds?.payable?.outstanding || 0);
                }}
              >
                <SelectTrigger className="mt-1" data-testid="remit-section">
                  <SelectValue placeholder="All sections" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All sections</SelectItem>
                  {sections.map((r) => (
                    <SelectItem key={r.section} value={r.section}>
                      {r.section} · {formatMoney(r.outstanding, ccy)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Challan no. (optional)</label>
              <Input
                value={challan}
                onChange={(e) => setChallan(e.target.value)}
                className="mt-1"
                data-testid="remit-challan"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="remit-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} className="gap-2" data-testid="remit-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Landmark className="h-4 w-4" />}
            Remit {formatMoney(Number(amount) || 0, ccy)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TdsPanel({ tds, periods, accounts, onRemitted }) {
  const [open, setOpen] = useState(false);
  const [showMonths, setShowMonths] = useState(false);
  const ccy = accounts?.[0]?.currency || tds?.currency || "INR";
  if (!tds) return null;
  const payable = tds.payable || {};
  const receivable = tds.receivable || {};
  if ((payable.withheld || 0) <= 0 && (receivable.accrued || 0) <= 0) return null;
  const rows = (periods?.periods || []).filter((r) => (r.withheld || 0) > 0);
  const monthLabel = (p) => {
    try {
      const [y, m] = p.split("-");
      return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
    } catch {
      return p;
    }
  };
  return (
    <Card className="p-4" data-testid="tds-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              TDS Payable (to deposit)
            </p>
            <p className="font-heading text-2xl mt-1" data-testid="tds-payable-outstanding">
              {formatMoney(payable.outstanding || 0, ccy)}
            </p>
            <p className="text-[11px] text-muted-foreground">
              withheld {formatMoney(payable.withheld || 0, ccy)} · remitted {formatMoney(payable.remitted || 0, ccy)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              TDS Receivable (26AS)
            </p>
            <p className="font-heading text-2xl mt-1" data-testid="tds-receivable-accrued">
              {formatMoney(receivable.accrued || 0, ccy)}
            </p>
            <p className="text-[11px] text-muted-foreground">claimed in your income-tax return</p>
          </div>
          {(payable.by_section || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 max-w-sm">
              {payable.by_section
                .filter((r) => (r.outstanding || 0) > 0)
                .map((r) => (
                  <Badge key={r.section} variant="outline" className="text-[10px]">
                    {r.section}: {formatMoney(r.outstanding, ccy)}
                  </Badge>
                ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {rows.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setShowMonths((s) => !s)}
              className="gap-2"
              data-testid="tds-by-month-btn"
            >
              <CalendarClock className="h-4 w-4" /> {showMonths ? "Hide" : "By month"}
            </Button>
          )}
          <Button
            onClick={() => setOpen(true)}
            disabled={(payable.outstanding || 0) <= 0}
            className="gap-2"
            data-testid="remit-tds-btn"
          >
            <Landmark className="h-4 w-4" /> Remit to Government
          </Button>
        </div>
      </div>

      {showMonths && rows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-lg border border-border" data-testid="tds-month-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Month</th>
                <th className="px-3 py-2">Section</th>
                <th className="px-3 py-2 text-right">Withheld</th>
                <th className="px-3 py-2 text-right">Remitted</th>
                <th className="px-3 py-2 text-right">To deposit</th>
                <th className="px-3 py-2">Due by</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.period}-${r.section}`} className="border-b border-border/50 last:border-0" data-testid="tds-month-row">
                  <td className="px-3 py-2 whitespace-nowrap">{monthLabel(r.period)}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{r.section}</Badge></td>
                  <td className="px-3 py-2 text-right font-mono">{formatMoney(r.withheld, ccy)}</td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">{formatMoney(r.remitted, ccy)}</td>
                  <td className="px-3 py-2 text-right font-mono font-medium">{formatMoney(r.outstanding, ccy)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{r.due_date ? formatDate(r.due_date) : "—"}</td>
                  <td className="px-3 py-2">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${r.status === "Overdue" ? "border-danger/40 text-danger" : r.status === "Paid" ? "border-success/40 text-success" : "border-warning/40 text-warning"}`}
                    >
                      {r.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RemitTdsDialog open={open} onOpenChange={setOpen} accounts={accounts} tds={tds} onRemitted={onRemitted} />
    </Card>
  );
}

export default function Reconciliation() {
  const [status, setStatus] = useState(
    () => new URLSearchParams(window.location.search).get("status") || "unmatched"
  );
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tdsSummary, setTdsSummary] = useState(null);
  const [tdsPeriods, setTdsPeriods] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(
    () => new URLSearchParams(window.location.search).get("account_id") || "all"
  );
  const [from, setFrom] = useState(
    () => new URLSearchParams(window.location.search).get("from") || ""
  );
  const [to, setTo] = useState(() => new URLSearchParams(window.location.search).get("to") || "");
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api
      .get("/entities/bank-accounts")
      .then(({ data }) => setAccounts(data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = [
        `status=${status}`,
        "limit=300",
        accountId !== "all" ? `bank_account_id=${accountId}` : "",
        from ? `from_date=${from}` : "",
        to ? `to_date=${to}` : "",
      ]
        .filter(Boolean)
        .join("&");
      const [{ data }, s, tds, tp] = await Promise.all([
        api.get(`/recon/bank-txns?${qs}`),
        api.get("/recon/summary"),
        api.get("/recon/tds/summary"),
        api.get("/recon/tds/by-period"),
      ]);
      setTxns(data.txns || []);
      setSummary(s.data);
      setTdsSummary(tds.data);
      setTdsPeriods(tp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load bank transactions");
    } finally {
      setLoading(false);
    }
  }, [status, accountId, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const highIds = useMemo(
    () =>
      txns
        .filter((t) => t.status === "unmatched" && t.suggestions?.[0]?.confidence === "high")
        .map((t) => t.id),
    [txns]
  );

  const bulkConfirm = async () => {
    if (!highIds.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/recon/bulk-confirm", {
        txn_ids: highIds,
        min_confidence: "high",
      });
      toast.success(`${data.confirmed} settlement(s) posted · ${data.skipped} left for review`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk confirm failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="reconciliation-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Bank Reconciliation</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Invoices sit in the books as accruals. Bank lines land here unposted — confirm the invoice
            they settle and the journal entry (Dr Payable / Cr Bank) is written for you.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            className="gap-2"
            data-testid="add-bank-txn-btn"
          >
            <Plus className="h-4 w-4" />
            Add transaction
          </Button>
          <Button
            onClick={bulkConfirm}
            disabled={!highIds.length || bulkBusy}
            className="gap-2"
            data-testid="bulk-confirm-btn"
          >
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Confirm {highIds.length} high-confidence match{highIds.length === 1 ? "" : "es"}
          </Button>
        </div>
      </div>

      <BankTxnDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        txn={editing}
        accounts={accounts}
        onSaved={load}
      />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Awaiting match", summary.unmatched, "recon-stat-unmatched"],
            ["High-confidence suggestions", summary.high_confidence_suggestions, "recon-stat-high"],
            ["Open vendor invoices", summary.open_payables, "recon-stat-payables"],
            ["Open client invoices", summary.open_receivables, "recon-stat-receivables"],
          ].map(([label, value, tid]) => (
            <Card key={tid} className="p-4" data-testid={tid}>
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
              <p className="font-heading text-2xl mt-1.5">{value}</p>
            </Card>
          ))}
        </div>
      )}

      <TdsPanel tds={tdsSummary} periods={tdsPeriods} accounts={accounts} onRemitted={load} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setStatus(t.id)}
              data-testid={`recon-tab-${t.id}`}
              className={`px-3 py-1.5 rounded-md text-xs transition-colors ${
                status === t.id
                  ? "bg-brand/15 text-foreground border border-brand/30"
                  : "text-muted-foreground border border-white/5 hover:bg-white/[0.04]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger className="w-[190px] h-9" data-testid="recon-account-filter">
              <SelectValue placeholder="All accounts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All bank accounts</SelectItem>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="recon-from-filter"
            className="h-9 rounded-md bg-background border border-input px-2 text-xs"
          />
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="recon-to-filter"
            className="h-9 rounded-md bg-background border border-input px-2 text-xs"
          />
          {(accountId !== "all" || from || to) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAccountId("all");
                setFrom("");
                setTo("");
              }}
              data-testid="recon-clear-filters"
              className="h-9 text-xs text-muted-foreground"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-24 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : txns.length === 0 ? (
        <Card className="p-10 text-center" data-testid="recon-empty">
          <p className="text-sm text-muted-foreground">
            Nothing here. Upload a bank statement from Bank Balances to stage transactions for matching.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {txns.map((t) => (
            <TxnCard
              key={t.id}
              txn={t}
              onRefresh={load}
              onEdit={(x) => {
                setEditing(x);
                setDialogOpen(true);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
